import React from "react";
import { useNavigate } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { useSearch, toUrlParams } from "../../contexts/SearchContext";
import { useOrg } from "../../contexts/OrganizationContext";
import { useMap } from "../../contexts/MapContext";
import { apiService } from "../../services/index";
import { taxonomyService } from "../../services/taxonomyService";
import { useAutocompleteKeyboard } from "../../hooks/useAutocompleteKeyboard";
import SearchFiltersPanel from "./SearchFiltersPanel";
import HeaderCartButton from "./HeaderCartButton";
import SearchSidebarForm from "./SearchSidebarForm";
import SearchSidebarSelections from "./SearchSidebarSelections";
import SearchSidebarSuggestions from "./SearchSidebarSuggestions";

// Mirrors TransparentSearch's synchronous derivation: don't rely solely on async
// geocoding to populate service_area, since Google's place-type classification
// (e.g. for Connecticut towns) can miss and silently leave it empty.
function deriveServiceArea(location, stateName) {
  const cityFromLocation = (location || stateName || "").split(",")[0].trim();
  return cityFromLocation.toLowerCase();
}

function SidebarSearchHeader({ alwaysShowFilters = false }) {
  const navigate = useNavigate();
  const { organization } = useOrg();
  const { params, targetPopulations, categorySelections, search, setParams, resetFilters, clearAll } = useSearch();
  const mapContext = useMap();

  const [showFilters, setShowFilters] = React.useState(false);
  const [termInput, setTermInput] = React.useState("");
  const [locationInput, setLocationInput] = React.useState(params.location || "");
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [problemStatements, setProblemStatements] = React.useState([]);
  const [showMobileFilters, setShowMobileFilters] = React.useState(false);
  const [searchActionPending, setSearchActionPending] = React.useState(false);

  // Sync local input when location changes from URL navigation (not user typing)
  React.useEffect(() => {
    setLocationInput(params.location || "");
  }, [params.location]);

  React.useEffect(() => {
    if (!alwaysShowFilters) return undefined;
    function handleOpenFilters() {
      setShowMobileFilters(true);
    }
    window.addEventListener("openSearchFilters", handleOpenFilters);
    return () => window.removeEventListener("openSearchFilters", handleOpenFilters);
  }, [alwaysShowFilters]);

  const suggestionsListRef = React.useRef(null);

  const { hoveredIndex, setHoveredIndex, handleKeyDown: handleSearchKeyDown } = useAutocompleteKeyboard({
    suggestions,
    showSuggestions,
    setShowSuggestions,
    onSelect: handleSuggestionSelect,
    listRef: suggestionsListRef,
  });

  const searchInputRef = React.useRef(null);
  const mobileSearchInputRef = React.useRef(null);
  const searchFormRef = React.useRef(null);
  const fetchIdRef = React.useRef(0);
  const debounceTimerRef = React.useRef(null);

  const advancedFilters = organization.advanceSearchFilters || {};
  const showAdvancedFilters = Object.values(advancedFilters).some(Boolean);

  const FILTER_KEYS = ['radius', 'tp', 'gender', 'age_range', 'open_now', 'open_on', 'area_sort', 'referral'];
  const appliedFiltersCount = FILTER_KEYS.filter((k) => {
    const v = params[k];
    return Array.isArray(v) ? v.length > 0 : !!v;
  }).length;

  const hasNonDefaultServiceArea =
    params.service_area &&
    params.service_area !== (organization.serviceLocale?.stateName || "").toLowerCase();
  const showCombinedSearchButton = showAdvancedFilters && alwaysShowFilters;

  const terms = params.terms || [];
  const taxonomyCodes = params.taxonomy_code || [];

  // Load problem statements once
  React.useEffect(() => {
    apiService.get("/problem_statements")
      .then((data) => setProblemStatements(data || []))
      .catch(() => {});
  }, []);

  // Close suggestions on outside click
  React.useEffect(() => {
    function handleClickOutside(e) {
      if (searchFormRef.current && !searchFormRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (!showMobileFilters) return undefined;

    const scrollY = window.scrollY;
    const bodyStyle = document.body.style;
    const htmlStyle = document.documentElement.style;
    const previousBodyStyles = {
      overflow: bodyStyle.overflow,
      position: bodyStyle.position,
      top: bodyStyle.top,
      width: bodyStyle.width,
    };
    const previousHtmlOverflow = htmlStyle.overflow;

    htmlStyle.overflow = "hidden";
    bodyStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.width = "100%";

    return () => {
      htmlStyle.overflow = previousHtmlOverflow;
      bodyStyle.overflow = previousBodyStyles.overflow;
      bodyStyle.position = previousBodyStyles.position;
      bodyStyle.top = previousBodyStyles.top;
      bodyStyle.width = previousBodyStyles.width;
      window.scrollTo(0, scrollY);
    };
  }, [showMobileFilters]);

  function debouncedFetchSuggestions(val) {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(val), 250);
  }

  // Watch for native input events (Selenium/Capybara compatibility)
  React.useEffect(() => {
    const el = searchInputRef.current;
    if (!el) return;
    function onNativeInput() {
      const val = el.value;
      setTermInput(val);
      debouncedFetchSuggestions(val);
    }
    el.addEventListener("input", onNativeInput);
    return () => el.removeEventListener("input", onNativeInput);
  }, [problemStatements, taxonomyCodes]);

  function filterProblemStatements(term) {
    const selectedTaxonomyCodes = taxonomyCodes.map(String);
    const mapped = (items) =>
      items
        .filter((ps) => (ps.taxonomy_ids || []).some((id) => !selectedTaxonomyCodes.includes(String(id))))
        .map((ps) => ({
          label: ps.statement,
          taxonomy_ids: ps.taxonomy_ids,
          combined_taxonomy_ids: ps.combined_taxonomy_ids,
          type: "suggested",
        }));
    if (!term) return mapped(problemStatements);
    const lower = term.toLowerCase();
    return mapped(problemStatements.filter((ps) => ps.statement.toLowerCase().includes(lower)));
  }

  async function fetchSuggestions(term) {
    const fetchId = ++fetchIdRef.current;
    if (term.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const selectedTaxonomyCodes = taxonomyCodes.map(String);
    const suggested = filterProblemStatements(term);
    try {
      const taxonomies = await taxonomyService.search(term);
      if (fetchId !== fetchIdRef.current) return;
      const formatted = (taxonomies || [])
        .filter((t) => !selectedTaxonomyCodes.includes(String(t.id)))
        .map((t) => ({
          label: t.label || t.name,
          code: t.id,
          category: t.category,
          type: "taxonomy",
        }));
      setSuggestions([...suggested, ...formatted]);
      setShowSuggestions(suggested.length + formatted.length > 0);
    } catch {
      if (fetchId !== fetchIdRef.current) return;
      setSuggestions(suggested);
      setShowSuggestions(suggested.length > 0);
    }
  }

  function handleSuggestionSelect(suggestion) {
    setShowSuggestions(false);
    setTermInput("");

    let newTaxonomyCodes = taxonomyCodes;
    let newTerms = terms;
    const selectedTaxonomyCodes = newTaxonomyCodes.map(String);

    if (suggestion.taxonomy_ids) {
      // Problem statement: add its taxonomy codes (avoid duplicates)
      const toAdd = suggestion.taxonomy_ids.filter((id) => !selectedTaxonomyCodes.includes(String(id)));
      newTaxonomyCodes = [...newTaxonomyCodes, ...toAdd];
    } else if (suggestion.code) {
      // Taxonomy item: add its code if not already selected
      if (!selectedTaxonomyCodes.includes(String(suggestion.code))) {
        newTaxonomyCodes = [...newTaxonomyCodes, suggestion.code];
      }
    }

    setParams({ taxonomy_code: newTaxonomyCodes, terms: newTerms, page: 1 });
    navigate(toUrlParams({ ...params, taxonomy_code: newTaxonomyCodes, terms: newTerms, page: 1 }));
  }

  function handleSearchInputFocus() {
    if (termInput.length >= 3 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }

  function handleSearchInputChange(e) {
    const val = e.target.value;
    setTermInput(val);
    setSearchActionPending(true);
    debouncedFetchSuggestions(val);
  }

  function updateFilterParams(nextParams) {
    setSearchActionPending(true);
    setParams(nextParams);
  }

  function handleLocationInputChange(value) {
    setLocationInput(value);
    setSearchActionPending(true);
  }

  async function runSearch({ closeFilters = false } = {}) {
    const newTerms = termInput.trim() ? [...terms, termInput.trim()] : terms;
    setShowSuggestions(false);
    // If the user typed a new location without selecting from autocomplete, apply it with null coords
    const locationChanged = locationInput !== (params.location || "");
    const locationOverride = locationChanged
      ? {
          location: locationInput,
          coords: null,
          service_area: deriveServiceArea(locationInput, organization.serviceLocale?.stateName),
        }
      : {};

    if (params.radius) {
      mapContext.clearMapZoom();
    }

    setTermInput("");
    await search({ terms: newTerms, ...locationOverride, page: 1 });
    setSearchActionPending(false);

    if (closeFilters && !alwaysShowFilters) {
      setShowFilters(false);
    }
  }

  function handleSearch(e) {
    e.preventDefault();
    runSearch();
  }

  function handleTermKeyDown(e) {
    handleSearchKeyDown(e);
    if (e.defaultPrevented || e.key !== "Enter" || e.nativeEvent?.isComposing) return;

    e.preventDefault();
    runSearch({ closeFilters: showCombinedSearchButton || showMobileFilters });
    if (showMobileFilters) setShowMobileFilters(false);
  }

  // Items to display: category selections (from API) followed by raw text terms.
  // A deep link can set a term and its matching taxonomy_code at the same time
  // (e.g. ?terms=Food+Pantries&taxonomy_code=7), so drop any raw term that
  // duplicates an already-shown category label to avoid a repeated chip.
  const categoryNames = categorySelections.map((c) => c.name.toLowerCase());
  const visibleTerms = terms.filter((t) => !categoryNames.includes(t.toLowerCase()));
  const selectionItems = [
    ...categorySelections.map((c) => ({ label: c.name, id: c.id, isCategory: true })),
    ...visibleTerms.map((t) => ({ label: t, isCategory: false })),
  ];

  function handleRemoveSelection(index) {
    const item = selectionItems[index];
    if (item.isCategory) {
      const newCodes = taxonomyCodes.filter((code) => code !== item.id);
      setParams({ taxonomy_code: newCodes });
      search({ taxonomy_code: newCodes, page: 1 });
    } else {
      const newTerms = terms.filter((t) => t !== item.label);
      setParams({ terms: newTerms });
      search({ terms: newTerms, page: 1 });
    }
  }

  function handleClearAll(e) {
    e.preventDefault();
    clearAll();
    navigate("/search");
  }

  function highlightTerm(label, term) {
    if (!term || term.length < 2) return label;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return label.replace(new RegExp(escaped, "ig"), (match) => `<strong>${match}</strong>`);
  }

  function handleApplyFilters() {
    runSearch({ closeFilters: true });
    setShowMobileFilters(false);
  }

  function handleClearFilters() {
    resetFilters();
    setSearchActionPending(false);
    search();
  }

  const filtersVisible = showAdvancedFilters && (alwaysShowFilters || showFilters);
  const filterButtonLabel = appliedFiltersCount > 0
    ? `View ${appliedFiltersCount} ${appliedFiltersCount === 1 ? "Filter" : "Filters"}`
    : "View Filters";
  const locationSummary = locationInput || `all of ${organization.serviceLocale?.stateName || "the selected area"}`;
  function renderFilterPanel() {
    if (!showAdvancedFilters) return null;

    return (
      <SearchFiltersPanel
        advancedFilters={advancedFilters}
        targetPopulations={targetPopulations}
        selectedTargetPopulations={params.tp || []}
        onTargetPopulationChange={(value) => updateFilterParams({ tp: value })}
        hasNonDefaultServiceArea={hasNonDefaultServiceArea}
        radius={params.radius}
        onRadiusChange={(value) => updateFilterParams({ radius: value })}
        openNow={params.open_now}
        openOn={params.open_on}
        onOpenNowChange={(value) => updateFilterParams({ open_now: value })}
        onOpenOnChange={(value) => updateFilterParams({ open_on: value })}
        gender={params.gender}
        onGenderChange={(value) => updateFilterParams({ gender: value })}
        ageRange={params.age_range}
        onAgeRangeChange={(value) => updateFilterParams({ age_range: value })}
        areaSort={params.area_sort}
        onAreaSortChange={(value) => updateFilterParams({ area_sort: value })}
        referral={params.referral}
        onReferralChange={(value) => updateFilterParams({ referral: value })}
        stateAbbr={organization.serviceLocale?.abbr}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        applyButtonLabel={showCombinedSearchButton ? "Apply and Search" : undefined}
        applyButtonDisabled={!searchActionPending}
      />
    );
  }
  const LISTBOX_ID = "sidebar-search-suggestions";
  const MOBILE_LISTBOX_ID = "sidebar-search-suggestions-mobile";

  // Suggestion state is shared between the sidebar form and the mobile filter
  // drawer form, but only one is interactive at a time. Render the dropdown in
  // the active context so the shared suggestionsListRef points at the visible
  // list — otherwise the drawer's typeahead has nowhere to paint.
  function renderTermDropdown(listboxId) {
    return (
      <SearchSidebarSuggestions
        showSuggestions={showSuggestions}
        suggestions={suggestions}
        suggestionsListRef={suggestionsListRef}
        hoveredIndex={hoveredIndex}
        onHoveredIndexChange={setHoveredIndex}
        onSelectSuggestion={handleSuggestionSelect}
        getHighlightedLabel={(label) => highlightTerm(label, termInput)}
        listboxId={listboxId}
      />
    );
  }

  function buildInputAriaProps(listboxId, isActive) {
    const listboxVisible = isActive && showSuggestions && suggestions.length > 0;
    return {
      role: "combobox",
      "aria-haspopup": "listbox",
      "aria-autocomplete": "list",
      "aria-expanded": listboxVisible,
      "aria-controls": listboxId,
      "aria-activedescendant": isActive && hoveredIndex !== null ? `${listboxId}-option-${hoveredIndex}` : undefined,
    };
  }

  const searchControls = (
    <>
      <SearchSidebarForm
        searchInputRef={searchInputRef}
        termInput={termInput}
        onSearchInputChange={handleSearchInputChange}
        onSearchInputFocus={handleSearchInputFocus}
        onSearchKeyDown={handleTermKeyDown}
        locationInput={locationInput}
        onLocationInputChange={handleLocationInputChange}
        onPlaceSelect={(location, coords) => {
          setLocationInput(location);
          setSearchActionPending(true);
          setParams({ location, coords, service_area: deriveServiceArea(location, organization.serviceLocale?.stateName) });
        }}
        onSubmit={handleSearch}
        showSubmitButton={!showCombinedSearchButton}
        termDropdown={showMobileFilters ? null : renderTermDropdown(LISTBOX_ID)}
        inputAriaProps={buildInputAriaProps(LISTBOX_ID, !showMobileFilters)}
      />

      <div className="mobile-search-location-summary">
        in {locationSummary}{" "}
        <button
          type="button"
          onClick={() => setShowMobileFilters(true)}
        >
          Update Location
        </button>
      </div>

      {selectionItems.length > 0 && (
        <SearchSidebarSelections
          selectionItems={selectionItems}
          onRemoveSelection={handleRemoveSelection}
          onClearAll={handleClearAll}
        />
      )}
    </>
  );
  const mobileDrawerSearchControls = (
    <>
      <SearchSidebarForm
        searchInputRef={mobileSearchInputRef}
        termInput={termInput}
        onSearchInputChange={handleSearchInputChange}
        onSearchInputFocus={handleSearchInputFocus}
        onSearchKeyDown={handleTermKeyDown}
        locationInput={locationInput}
        onLocationInputChange={handleLocationInputChange}
        onPlaceSelect={(location, coords) => {
          setLocationInput(location);
          setSearchActionPending(true);
          setParams({ location, coords, service_area: deriveServiceArea(location, organization.serviceLocale?.stateName) });
        }}
        onSubmit={handleSearch}
        showSubmitButton={false}
        idSuffix="-mobile-filter"
        termDropdown={showMobileFilters ? renderTermDropdown(MOBILE_LISTBOX_ID) : null}
        inputAriaProps={buildInputAriaProps(MOBILE_LISTBOX_ID, showMobileFilters)}
      />

      {selectionItems.length > 0 && (
        <SearchSidebarSelections
          selectionItems={selectionItems}
          onRemoveSelection={handleRemoveSelection}
          onClearAll={handleClearAll}
        />
      )}
    </>
  );

  return (
    <>
      <div className="sidebar-search-header">
        {alwaysShowFilters && (
          <HeaderCartButton className="sidebar-resource-list-region" />
        )}

        <div className="search-region search" ref={searchFormRef}>
          {searchControls}

          {alwaysShowFilters && (
            <button
              className="mobile-filter-drawer-trigger"
              type="button"
              onClick={() => setShowMobileFilters(true)}
            >
              <SlidersHorizontal aria-hidden="true" size={18} strokeWidth={2} />
              <span>{filterButtonLabel}</span>
            </button>
          )}
        </div>
      </div>

      {showAdvancedFilters && (
        <>
          {!alwaysShowFilters && (
            <div className="filters-header pointer">
              <button
                className="btn btn-secondary toggle-filters"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="ico ico-equalizer" />
                <span>
                  {showFilters
                    ? "Hide Advanced Filters"
                    : appliedFiltersCount > 0
                      ? `${appliedFiltersCount} ${appliedFiltersCount === 1 ? "Filter" : "Filters"} Applied`
                      : "Show Advanced Filters"}
                </span>
                <i className={`toggle-arrow ${showFilters ? "icon-arrow-up" : "icon-arrow-down"}`} />
              </button>
            </div>
          )}

          <div className="filters-container">
            {filtersVisible && (
              renderFilterPanel()
            )}
          </div>
        </>
      )}

      {alwaysShowFilters && (
        <div className={`mobile-filter-drawer${showMobileFilters ? " open" : ""}`} aria-hidden={!showMobileFilters}>
          <div className="mobile-filter-drawer-backdrop" onClick={() => setShowMobileFilters(false)} />
          <section className="mobile-filter-drawer-panel" role="dialog" aria-modal="true" aria-label="Filter and Search">
            <header className="mobile-filter-drawer-header">
              <h2>Filter &amp; Search</h2>
              <button
                className="mobile-filter-drawer-close"
                type="button"
                aria-label="Close filters"
                onClick={() => setShowMobileFilters(false)}
              >
                <X aria-hidden="true" size={18} strokeWidth={2} />
              </button>
            </header>
            <div className="mobile-filter-drawer-body">
              <div className="mobile-filter-drawer-search">
                {mobileDrawerSearchControls}
              </div>
              {showAdvancedFilters && (
                <div className="mobile-filter-drawer-filters">
                  {renderFilterPanel()}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default SidebarSearchHeader;
